/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useEffect } from "react";

import Cookies from "js-cookie";
import { NextRouter, useRouter } from "next/router";
import { isElectron } from "react-device-detect";
import { serializeError } from "serialize-error";

import { clientLogger } from "src/lib/logging/datadog";
import { parseJwt } from "src/lib/utils/parseJwt";

if (typeof window !== "undefined") {
  // rewrites don't work in electron, but logrocket will without proxy
  window._lrAsyncScript = isElectron ? "https://cdn.lr-in-prod.com/logger.min.js" : "/logger.min.js";
}

const usePageTracking = (): void => {
  const router = useRouter();
  useEffect(() => {
    trackPageViews(router);
  }, [router]);
};

export default usePageTracking;

function trackPageViews(router: NextRouter): void {
  const token = localStorage?.getItem("token") || Cookies.get("token");
  const username = parseJwt(token || "{}")?.username || null;
  if (username === "vht" || typeof window === "undefined") return;
  try {
    window.gtag("event", "page_view", {
      page_location: document.baseURI, // The full URL is required.
      page_title: decodeURI(router.pathname).substring(1).replaceAll("/", " - "),
    });
  } catch (e) {
    clientLogger.error("Error with google page tracking", { inner: serializeError(e) });
  }

  try {
    window.analytics.page({
      isOwnPage: username ? router.asPath?.includes(`plate/${username}/`) : false,
      isOwnProfile: username ? router.asPath?.endsWith(`/user/${username}`) : false,
      isSignedIn: !!token,
    });
  } catch (e) {
    clientLogger.error("Error with segment page tracking", { inner: serializeError(e) });
  }
}
